import { apiClient } from '@shared/lib/axios';

import { clinicApiPath } from '@/shared/lib/apiPath';

export interface ConsentDocumentPrintTemplate {
  id: number;
  title: string;
  is_default: boolean;
  suffix_type_id: number;
  representative_name: string | null;
}

export interface ConsentDocumentPrintMassager {
  id: number;
  serial_number: number | null;
  name: string;
  address1?: string | null;
  address2?: string | null;
  address3?: string | null;
}

export interface ConsentDocumentPrintMassagerLocationOption {
  id: number;
  massager_id: number;
  label: string;
}

export interface ConsentDocumentPrintSuffixType {
  id: number;
  name: string;
}

export interface ConsentDocumentPrintInitData {
  consent_id: number;
  document_type_id: number;
  document_template_id: number | null;
  document_templates: ConsentDocumentPrintTemplate[];
  hospital_doctor_name: string;
  hospital_name: string;
  doctor_name: string;
  suffix_type_id: number | null;
  representative_name: string;
  massager_id: number | null;
  massagers: ConsentDocumentPrintMassager[];
  massager_location_options?: ConsentDocumentPrintMassagerLocationOption[];
  suffix_types: ConsentDocumentPrintSuffixType[];
  is_print: boolean;
  is_representative_name: boolean;
  is_massager: boolean;
  is_practitioner_name: boolean;
  is_massager_location: boolean;
  is_practitioner_address: boolean;
  is_massager_location_address: boolean;
  patient_name: string;
}

export interface ConsentDocumentPrintInitResponse {
  success: boolean;
  message: string;
  data: ConsentDocumentPrintInitData;
}

export interface ConsentDocumentPrintPayload {
  document_type_id: number;
  document_template_id: number;
  hospital_doctor_name: string;
  hospital_name: string;
  doctor_name: string;
  suffix_type_id: number;
  representative_name?: string;
  massager_id: number;
  massager_location_id?: number;
  is_print?: boolean;
  is_representative_name?: boolean;
  is_massager?: boolean;
  is_practitioner_name?: boolean;
  is_massager_location?: boolean;
  is_practitioner_address?: boolean;
  is_massager_location_address?: boolean;
}

export async function fetchConsentDocumentPrintInit(
  siteType: string,
  clinicNumber: string,
  consentId: number,
  documentTypeId: number,
  serviceType: string
): Promise<ConsentDocumentPrintInitData> {
  const response = await apiClient.get<ConsentDocumentPrintInitResponse>(
    clinicApiPath(siteType, clinicNumber, `/consents/${consentId}/document-print/${documentTypeId}/init`),
    {
      headers: {
        'X-Service-Type': serviceType,
      },
    }
  );

  return response.data.data;
}

export async function outputConsentDocumentPrintPdf(
  siteType: string,
  clinicNumber: string,
  consentId: number,
  payload: ConsentDocumentPrintPayload,
  serviceType: string
): Promise<Blob> {
  const response = await apiClient.post(
    clinicApiPath(siteType, clinicNumber, `/consents/${consentId}/document-print/output`),
    payload,
    {
      headers: {
        'X-Service-Type': serviceType,
      },
      responseType: 'blob',
    }
  );

  return response.data as Blob;
}

// ---- 施術録 print (treatment record) ----

export interface TreatmentRecordPrintInitData {
  patient: {
    id: number;
    serial_number: string;
    name: string;
    name_kana: string;
  };
  consent: {
    id: number;
    treatment_term_from: string;
    treatment_term_to: string;
  };
  rezept_ids: number[];
  visit_year_months: number[];
  flags: Record<string, boolean>;
  office_name: string;
  consents_doctor_name_kana: string;
  length: string;
}

export interface TreatmentRecordPrintInitResponse {
  success: boolean;
  message: string;
  data: TreatmentRecordPrintInitData;
}

export interface TreatmentRecordPrintPayload {
  rezept_ids: number[];
  /** 1 = 表面 only, 2 = 裏面 only, 3 = 両面. Defaults to 1. */
  output_radio?: 1 | 2 | 3;
  /** 期間指定 narrow range (YYYYMM inclusive). Null = no filter. */
  search_treatment_from?: number | null;
  search_treatment_to?: number | null;
  office_name?: string | null;
  consents_doctor_name_kana?: string | null;
  length?: string | null;
  [flag: string]: boolean | number | number[] | string | null | undefined;
}

export interface TreatmentRecordPreviewTicketResult {
  url: string;
  /**
   * 「月に複数の保険証があります。表面は変更後の内容で印刷します。」— set when the
   * consent scope contains more than one 保険証. 表面 is merged into one page, so the
   * user is told which card wins before the tab opens. Null = no warning.
   * Spec: Docs/R2A2-treatment-record-front-spec.md §4.3
   */
  multi_hic_warning: string | null;
}

interface TreatmentRecordPreviewTicketResponse {
  success: boolean;
  message: string;
  data: TreatmentRecordPreviewTicketResult;
}

export async function fetchTreatmentRecordPrintInit(
  siteType: string,
  clinicNumber: string,
  patientId: number,
  consentId: number,
  serviceType: string
): Promise<TreatmentRecordPrintInitData> {
  const response = await apiClient.get<TreatmentRecordPrintInitResponse>(
    clinicApiPath(siteType, clinicNumber, `/consents/${patientId}/${consentId}/treatment-record-print/init`),
    {
      headers: {
        'X-Service-Type': serviceType,
      },
    }
  );

  return response.data.data;
}

export async function outputTreatmentRecordPrintPdf(
  siteType: string,
  clinicNumber: string,
  patientId: number,
  consentId: number,
  payload: TreatmentRecordPrintPayload,
  serviceType: string
): Promise<Blob> {
  const response = await apiClient.post(
    clinicApiPath(siteType, clinicNumber, `/consents/${patientId}/${consentId}/treatment-record-print/output`),
    payload,
    {
      headers: {
        'X-Service-Type': serviceType,
      },
      responseType: 'blob',
    }
  );

  return response.data as Blob;
}

export async function createTreatmentRecordPrintPreviewTicket(
  siteType: string,
  clinicNumber: string,
  patientId: number,
  consentId: number,
  payload: TreatmentRecordPrintPayload,
  serviceType: string
): Promise<TreatmentRecordPreviewTicketResult> {
  const response = await apiClient.post<TreatmentRecordPreviewTicketResponse>(
    clinicApiPath(
      siteType,
      clinicNumber,
      `/consents/${patientId}/${consentId}/treatment-record-print/preview-ticket`
    ),
    payload,
    {
      headers: {
        'X-Service-Type': serviceType,
      },
    }
  );

  return response.data.data;
}

/** 同意書本体PDF（表面 / 裏面 / 両面）— legacy choice_type: a | b | ab */
export async function outputConsentFormPdf(
  siteType: string,
  clinicNumber: string,
  consentId: number,
  choiceType: 'a' | 'b' | 'ab',
  serviceType: string
): Promise<Blob> {
  const response = await apiClient.post(
    clinicApiPath(siteType, clinicNumber, `/consents/${consentId}/form-print/output`),
    { choice_type: choiceType },
    {
      headers: {
        'X-Service-Type': serviceType,
      },
      responseType: 'blob',
    }
  );

  return response.data as Blob;
}
